import { RefObject, useEffect } from 'react';

/**
 * Drives a Marquee track (children rendered twice, wraps at half its width)
 * with a rAF loop instead of the CSS animation, so the user can swipe/drag
 * it horizontally. Auto-scroll pauses while hovering (mouse) or dragging;
 * clicks after a real drag are suppressed so links don't fire.
 *
 * `itemCount` re-arms the effect once the track actually has children.
 */
export const useMarqueeDrag = (
	ref: RefObject<HTMLElement | null>,
	reverse: boolean,
	itemCount: number,
	duration = reverse ? 52 : 46
) => {
	useEffect(() => {
		const el = ref.current;
		if (!el || itemCount === 0) return;

		const reduced = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches;

		el.style.animation = 'none';
		el.style.touchAction = 'pan-y';
		el.style.cursor = 'grab';

		let half = el.scrollWidth / 2;
		const resizeObserver = new ResizeObserver(() => {
			half = el.scrollWidth / 2;
		});
		resizeObserver.observe(el);

		let offset = 0;
		let hoverPaused = false;
		let dragging = false;
		let startX = 0;
		let startOffset = 0;
		let moved = 0;

		// Keep offset in (-half, 0] so the doubled content wraps seamlessly.
		const apply = () => {
			if (half > 0) offset = ((offset % half) - half) % half;
			el.style.transform = `translateX(${offset}px)`;
		};

		let raf = 0;
		let last = performance.now();
		const tick = (now: number) => {
			const dt = Math.min((now - last) / 1000, 0.1);
			last = now;
			if (!reduced && !dragging && !hoverPaused && half > 0) {
				offset += ((reverse ? 1 : -1) * half * dt) / duration;
				apply();
			}
			raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);

		const onPointerDown = (e: PointerEvent) => {
			if (e.pointerType === 'mouse' && e.button !== 0) return;
			dragging = true;
			moved = 0;
			startX = e.clientX;
			startOffset = offset;
			el.style.cursor = 'grabbing';
		};
		const onPointerMove = (e: PointerEvent) => {
			if (!dragging) return;
			const dx = e.clientX - startX;
			moved = Math.max(moved, Math.abs(dx));
			offset = startOffset + dx;
			apply();
		};
		const onPointerEnd = () => {
			dragging = false;
			last = performance.now();
			el.style.cursor = 'grab';
		};
		const onClick = (e: MouseEvent) => {
			if (moved > 8) {
				e.preventDefault();
				e.stopPropagation();
			}
		};
		const onPointerEnter = (e: PointerEvent) => {
			if (e.pointerType === 'mouse') hoverPaused = true;
		};
		const onPointerLeave = () => {
			hoverPaused = false;
		};

		el.addEventListener('pointerdown', onPointerDown);
		window.addEventListener('pointermove', onPointerMove);
		window.addEventListener('pointerup', onPointerEnd);
		window.addEventListener('pointercancel', onPointerEnd);
		el.addEventListener('click', onClick, true);
		el.addEventListener('pointerenter', onPointerEnter);
		el.addEventListener('pointerleave', onPointerLeave);
		el.addEventListener('dragstart', (e) => e.preventDefault());

		return () => {
			cancelAnimationFrame(raf);
			resizeObserver.disconnect();
			el.removeEventListener('pointerdown', onPointerDown);
			window.removeEventListener('pointermove', onPointerMove);
			window.removeEventListener('pointerup', onPointerEnd);
			window.removeEventListener('pointercancel', onPointerEnd);
			el.removeEventListener('click', onClick, true);
			el.removeEventListener('pointerenter', onPointerEnter);
			el.removeEventListener('pointerleave', onPointerLeave);
			el.style.animation = '';
			el.style.transform = '';
			el.style.cursor = '';
		};
	}, [ref, reverse, itemCount, duration]);
};
